import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBar } from '@angular/material/snack-bar';
import { filter, Subject, take, takeUntil } from 'rxjs';
import { AuthCognitoService } from '../../auth/auth-cognito.service';
import { ContactsViewModel } from '../../contacts/contacts.view-model';
import { OrganizationsComponent } from '../../contacts/organizations/organizations.component';
import { AlertConfig, Organization, UpdateProfileRequest } from '../../contacts/user.service';
import { ConfigurationService } from '../configuration.service';
import { DashboardSectionHeaderComponent } from 'src/app/notification/dashboard/dashboard-section-header/dashboard-section-header.component';
import { ConfirmationDialogComponent } from 'src/app/shared/confirmation-dialog/confirmation-dialog.component';
import { MaterialModule } from 'src/app/shared/material/material.module';
import { SharedModule } from 'src/app/shared/shared.module';
import {
  DEFAULT_ALERT_CONFIG,
  DEFAULT_NOTIFY_WINDOW,
  DEFAULT_TARGET_TIME,
  PLATFORM_ALERT_LIMITS,
  resolveAlertConfig,
  SMS_BODY_MAX,
  SUBJECT_LABEL_MAX,
} from '../configuration.defaults';
import { ALERT_TIME_ZONE_OPTIONS } from '../alert-time-zones';
import {
  flagsToNotifyChannel,
  NOTIFY_CHANNELS,
  notifyChannelToFlags,
  NotifyChannel,
} from '../notify-channel.util';
import {
  fromRetryIntervalMinutes,
  INTERVAL_UNITS,
  IntervalUnit,
  isRetryIntervalAtLeast,
  toRetryIntervalMinutes,
} from '../interval-unit.util';

function retryIntervalAtLeastMinutes(minMinutes: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    const value = Number(raw);
    if (!Number.isFinite(value)) {
      return null;
    }
    const unit = (control.parent?.get('retryIntervalUnit')?.value ?? 'minutes') as IntervalUnit;
    if (!isRetryIntervalAtLeast(value, unit, minMinutes)) {
      return { minInterval: { minMinutes } };
    }
    return null;
  };
}
import { SMS_ALLOWED_TOKENS, smsTokensValidator } from '../sms-tokens.util';

@Component({
  selector: 'app-configuration-page',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    MatCheckboxModule,
    MatSidenavModule,
    ReactiveFormsModule,
    SharedModule,
    DashboardSectionHeaderComponent,
    OrganizationsComponent,
  ],
  templateUrl: './configuration-page.component.html',
  styleUrl: './configuration-page.component.scss',
  host: {
    class: 'contact-list',
  },
  changeDetection: ChangeDetectionStrategy.Default,
})
export class ConfigurationPageComponent implements OnInit, OnDestroy {
  #snackBar = inject(MatSnackBar);
  #dialog = inject(MatDialog);
  #authCognitoService = inject(AuthCognitoService);
  #contactsViewModel = inject(ContactsViewModel);
  #configurationService = inject(ConfigurationService);

  saving = signal(false);
  loadingConfig = signal(false);

  readonly subjectMax = SUBJECT_LABEL_MAX;
  readonly smsMax = SMS_BODY_MAX;
  readonly retryAttemptsMax = PLATFORM_ALERT_LIMITS.maxRetryAttempts;
  readonly minRetryIntervalMinutes = PLATFORM_ALERT_LIMITS.minRetryIntervalMinutes;
  readonly placeholders = DEFAULT_ALERT_CONFIG;
  readonly intervalUnits = INTERVAL_UNITS;
  readonly timeZoneOptions = ALERT_TIME_ZONE_OPTIONS;
  readonly notifyChannels = NOTIFY_CHANNELS;
  readonly smsTokenLabels = SMS_ALLOWED_TOKENS.map((token) => `{${token}}`);
  readonly smsPlaceholderHelp =
    'Placeholder items in {curly brackets} are populated by the system when the alert is sent. Allowed values:';

  form = new FormGroup({
    subjectLabel: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(SUBJECT_LABEL_MAX)],
    }),
    subjectLabelPlural: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(SUBJECT_LABEL_MAX)],
    }),
    retryAttempts: new FormControl<number | null>(null, {
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(PLATFORM_ALERT_LIMITS.maxRetryAttempts),
      ],
    }),
    retryInterval: new FormControl<number | null>(null, {
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.pattern(/^[1-9]\d*$/),
        retryIntervalAtLeastMinutes(PLATFORM_ALERT_LIMITS.minRetryIntervalMinutes),
      ],
    }),
    retryIntervalUnit: new FormControl<IntervalUnit>('minutes', { nonNullable: true }),
    targetTime: new FormControl(DEFAULT_TARGET_TIME, { nonNullable: true }),
    notifyWindowStart: new FormControl<string>(DEFAULT_NOTIFY_WINDOW.start, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    notifyWindowEnd: new FormControl<string>(DEFAULT_NOTIFY_WINDOW.end, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    notifyWindowTimeZone: new FormControl<string>(DEFAULT_NOTIFY_WINDOW.timeZone, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    notifyChannel: new FormControl<NotifyChannel>('both', { nonNullable: true }),
    smsBody: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(SMS_BODY_MAX), smsTokensValidator],
    }),
    pressZeroToConnect: new FormControl(DEFAULT_ALERT_CONFIG.pressZeroToConnect, {
      nonNullable: true,
    }),
    urlTitle: new FormControl('', { nonNullable: true }),
    urlHyperlink: new FormControl('', { nonNullable: true }),
  });

  locationsLoading = toSignal(this.#contactsViewModel.locationsLoading$);
  locations = toSignal(this.#contactsViewModel.locations$, { initialValue: [] });
  profile$ = this.#contactsViewModel.profile$;
  profileError$ = this.#contactsViewModel.profileError$;
  profile = toSignal(this.profile$);
  currentOrganization = toSignal(this.#contactsViewModel.currentOrganization$);

  @ViewChild('drawer', { static: true })
  organizationMenu!: MatSidenav;

  destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.profile$
      .pipe(
        filter((p) => !!p),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.#contactsViewModel.loadLocations();
      });

    this.#contactsViewModel.currentOrganization$
      .pipe(
        filter((org): org is Organization => !!org),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.resetFormEmpty();
        this.loadConfiguration();
      });

    this.profile$
      .pipe(
        take(1),
        filter((profile) => !profile),
      )
      .subscribe(() => {
        this.#contactsViewModel.loadProfile();
      });

    this.form.controls.retryIntervalUnit.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.form.controls.retryInterval.updateValueAndValidity();
        this.updateTargetTimeValidators();
      });
    this.updateTargetTimeValidators();

    this.profileError$
      .pipe(
        takeUntil(this.destroy$),
        filter((e) => !!e),
      )
      .subscribe(() => {
        const ref = this.#snackBar.open("Sorry, we couldn't load the user information.", 'Okay', {
          duration: 5000,
        });
        ref.afterDismissed().subscribe(() => {
          this.onLogout();
        });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  subjectLength(): number {
    return this.form.controls.subjectLabel.value.length;
  }

  pluralLength(): number {
    return this.form.controls.subjectLabelPlural.value.length;
  }

  smsLength(): number {
    return this.form.controls.smsBody.value.length;
  }

  smsTokenError(): string {
    const unknown = this.form.controls.smsBody.getError('smsTokens')?.unknown as string[] | undefined;
    if (!unknown?.length) {
      return '';
    }
    return `Unknown token: ${unknown.join(', ')}`;
  }

  onUseDefault(): void {
    const current = this.form.controls.smsBody.value.trim();
    if (!current) {
      this.applyDefaultSms();
      return;
    }

    this.#dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Replace message?',
          message: 'Replace the current message with the default template?',
          yesText: 'Replace',
          noText: 'Cancel',
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.applyDefaultSms();
        }
      });
  }

  private applyDefaultSms(): void {
    this.form.controls.smsBody.setValue(DEFAULT_ALERT_CONFIG.smsBody);
    this.form.controls.smsBody.markAsDirty();
  }

  onOrganizationSelected(org: Organization): void {
    this.organizationMenu.toggle();
    if (org.id !== this.profile()?.currentOrg) {
      const args: UpdateProfileRequest = {
        name: this.profile()?.name || '',
        currentOrg: org.id,
      };
      this.#contactsViewModel.updateOrganization(args, org);
    }
  }

  onSave(): void {
    if (this.form.invalid || this.saving()) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving.set(true);
    this.#configurationService.saveConfiguration(this.toAlertConfig()).subscribe({
      next: () => {
        this.form.markAsPristine();
        this.saving.set(false);
        this.#contactsViewModel.loadProfile();
        this.#snackBar.open('Configuration saved.', 'Okay', { duration: 4000 });
      },
      error: () => {
        this.saving.set(false);
        this.#snackBar.open('Could not save configuration. Please try again.', 'Okay', {
          duration: 5000,
        });
      },
    });
  }

  onLogout(): void {
    this.#authCognitoService.signOut().then(() => {
      sessionStorage.removeItem('patientId');
      sessionStorage.removeItem('organizationId');
    });
  }

  private loadConfiguration(): void {
    this.loadingConfig.set(true);
    this.#configurationService.getConfiguration().subscribe({
      next: ({ alertConfig }) => {
        if (alertConfig) {
          this.applyAlertConfig(alertConfig);
        } else {
          this.resetFormEmpty();
        }
        this.loadingConfig.set(false);
      },
      error: () => {
        this.loadingConfig.set(false);
        this.#snackBar.open('Could not load configuration. Please try again.', 'Okay', {
          duration: 5000,
        });
      },
    });
  }

  intervalPlaceholder(): string {
    return this.form.controls.retryIntervalUnit.value === 'minutes'
      ? String(DEFAULT_ALERT_CONFIG.retryIntervalMinutes)
      : '';
  }

  intervalInputMin(): number {
    return this.form.controls.retryIntervalUnit.value === 'minutes'
      ? PLATFORM_ALERT_LIMITS.minRetryIntervalMinutes
      : 1;
  }

  intervalUnitLabel(unit: IntervalUnit): string {
    if (unit === 'minutes') {
      return 'Minutes';
    }
    if (unit === 'hours') {
      return 'Hours';
    }
    return 'Days';
  }

  showDayTargetTime(): boolean {
    return this.form.controls.retryIntervalUnit.value === 'day';
  }

  notifyChannelLabel(channel: NotifyChannel): string {
    const labels: Record<NotifyChannel, string> = {
      both: 'Text and call',
      textOnly: 'Text only',
      callOnly: 'Call only',
    };
    return labels[channel];
  }

  private updateTargetTimeValidators(): void {
    const control = this.form.controls.targetTime;
    if (this.showDayTargetTime()) {
      control.setValidators([
        Validators.required,
        Validators.pattern(/^([01]\d|2[0-3]):[0-5]\d$/),
      ]);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  private applyAlertConfig(config: AlertConfig): void {
    const interval = fromRetryIntervalMinutes(config.retryIntervalMinutes);
    this.form.reset({
      subjectLabel: config.subjectLabel,
      subjectLabelPlural: config.subjectLabelPlural,
      retryAttempts: config.retryAttempts,
      retryInterval: interval.value,
      retryIntervalUnit: interval.unit,
      smsBody: config.smsBody,
      pressZeroToConnect: config.pressZeroToConnect,
      urlTitle: config.urlTitle,
      urlHyperlink: config.urlHyperlink,
      targetTime: config.targetTime ?? DEFAULT_TARGET_TIME,
      notifyWindowStart: config.notifyWindow?.start ?? DEFAULT_NOTIFY_WINDOW.start,
      notifyWindowEnd: config.notifyWindow?.end ?? DEFAULT_NOTIFY_WINDOW.end,
      notifyWindowTimeZone: config.notifyWindow?.timeZone ?? DEFAULT_NOTIFY_WINDOW.timeZone,
      notifyChannel: flagsToNotifyChannel(config.noText, config.noCall),
    });
  }

  private resetFormEmpty(): void {
    this.form.reset({
      subjectLabel: '',
      subjectLabelPlural: '',
      retryAttempts: null,
      retryInterval: null,
      retryIntervalUnit: 'minutes',
      smsBody: '',
      pressZeroToConnect: DEFAULT_ALERT_CONFIG.pressZeroToConnect,
      urlTitle: '',
      urlHyperlink: '',
      targetTime: DEFAULT_TARGET_TIME,
      notifyWindowStart: DEFAULT_NOTIFY_WINDOW.start,
      notifyWindowEnd: DEFAULT_NOTIFY_WINDOW.end,
      notifyWindowTimeZone: DEFAULT_NOTIFY_WINDOW.timeZone,
      notifyChannel: 'both',
    });
  }

  private toAlertConfig(): AlertConfig {
    const value = this.form.getRawValue();
    const channelFlags = notifyChannelToFlags(value.notifyChannel);
    return resolveAlertConfig({
      subjectLabel: value.subjectLabel.trim(),
      subjectLabelPlural: value.subjectLabelPlural.trim(),
      retryAttempts: Number(value.retryAttempts),
      retryIntervalMinutes: toRetryIntervalMinutes(
        Number(value.retryInterval),
        value.retryIntervalUnit,
      ),
      smsBody: value.smsBody.trim(),
      pressZeroToConnect: value.pressZeroToConnect,
      urlTitle: value.urlTitle.trim(),
      urlHyperlink: value.urlHyperlink.trim(),
      notifyWindow: {
        start: value.notifyWindowStart.trim(),
        end: value.notifyWindowEnd.trim(),
        timeZone: value.notifyWindowTimeZone.trim(),
      },
      targetTime: value.retryIntervalUnit === 'day' ? value.targetTime.trim() : undefined,
      noText: channelFlags.noText,
      noCall: channelFlags.noCall,
    });
  }
}
